<template>
  <div>
    <!-- Header -->
    <div class="d-flex flex-row mb-6 align-center">
      <div>
        <p class="text-2xl mb-0 primary--text font-weight-bold">Maillage Interne</p>
        <p class="text-sm grey--text mb-0">Sites branchés au maillage interne (related topics + liens Qdrant)</p>
      </div>
      <v-spacer></v-spacer>
      <v-btn color="primary" @click="openDialog()">
        <v-icon left>{{ icons.mdiPlus }}</v-icon>
        Brancher des sites
      </v-btn>
    </div>

    <v-card shadow="none" class="elevation-1">
      <v-data-table :headers="headers" :items="entries" :loading="loading" class="elevation-0">
        <template #[`item.site_name`]="{ item }">
          <span class="font-weight-bold">{{ item.site_name || `Site #${item.site_id}` }}</span>
        </template>

        <template #[`item.site_domain`]="{ item }">
          <code v-if="item.site_domain">{{ item.site_domain }}</code>
        </template>

        <template #[`item.is_active`]="{ item }">
          <v-switch
            :input-value="item.is_active"
            :loading="togglingId === item.id"
            :disabled="togglingId === item.id"
            color="success"
            dense
            hide-details
            class="mt-0 pt-0"
            @change="toggleActive(item, $event)"
          ></v-switch>
        </template>

        <template #[`item.created`]="{ item }">
          {{ item.created ? new Date(item.created).toLocaleDateString() : '' }}
        </template>

        <template #[`item.actions`]="{ item }">
          <div class="d-flex justify-end">
            <v-btn icon small color="error" @click="confirmDelete(item)">
              <v-icon small>{{ icons.mdiTrashCan }}</v-icon>
            </v-btn>
          </div>
        </template>

        <template #no-data>
          <span class="grey--text">Aucun site branché au maillage interne</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modal : brancher des sites -->
    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-title class="headline grey lighten-2">Brancher des sites</v-card-title>
        <v-card-text class="pt-6">
          <v-autocomplete
            v-model="selectedSiteIds"
            :items="availableSites"
            item-text="name"
            item-value="id"
            label="Sites"
            placeholder="Rechercher un ou plusieurs sites"
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            dense
            clearable
            hint="Seuls les sites non encore branchés sont proposés"
            persistent-hint
          >
            <!-- Le slot item écrase la case à cocher que Vuetify ajoute en mode multiple : on la remet. -->
            <template #item="{ item, attrs }">
              <v-list-item-action>
                <v-checkbox :input-value="attrs.inputValue" dense hide-details></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.domain">{{ item.domain }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">Annuler</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!selectedSiteIds.length" @click="save">
            Brancher{{ selectedSiteIds.length > 1 ? ` (${selectedSiteIds.length})` : '' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import { mdiPlus, mdiTrashCan } from '@mdi/js'
import axios from '@axios'
import store from '@/store'

const BASE_URL = '/aif/internal-linking-sites/'

export default {
  name: 'InternalLinkingSites',
  setup() {
    const loading = ref(false)
    const saving = ref(false)
    const togglingId = ref(null)
    const entries = ref([])

    const dialog = ref(false)
    const selectedSiteIds = ref([])

    const headers = [
      { text: 'Site', value: 'site_name' },
      { text: 'Domaine', value: 'site_domain' },
      { text: 'Actif', value: 'is_active', sortable: false },
      { text: 'Branché le', value: 'created' },
      { text: 'Actions', value: 'actions', align: 'end', sortable: false },
    ]

    const siteList = computed(() => store.getters['common/getSiteList'] ?? [])

    // Le back renvoie 409 dès qu'une ligne existe pour le site, même inactive :
    // un site débranché se réactive via le switch, il ne se recrée pas.
    const availableSites = computed(() => {
      const pluggedIds = new Set(entries.value.map(e => e.site_id))

      return siteList.value.filter(s => !pluggedIds.has(s.id))
    })

    const notifyError = (e, fallback) => {
      const detail = e.response?.data?.detail
      const text = typeof detail === 'string' ? detail : fallback
      store.dispatch('app/setSnackBar', { show: true, text, color: 'error' })
    }

    const fetchData = async () => {
      loading.value = true
      try {
        const res = await axios.get(BASE_URL)
        entries.value = res.data
      } catch (e) {
        notifyError(e, 'Erreur lors du chargement des sites branchés')
      } finally {
        loading.value = false
      }
    }

    const openDialog = () => {
      selectedSiteIds.value = []
      dialog.value = true
    }

    // Pas d'endpoint bulk côté back : un POST par site. On garde les échecs partiels
    // visibles plutôt que de laisser croire que tout est passé.
    const save = async () => {
      saving.value = true
      try {
        const ids = [...selectedSiteIds.value]
        const results = await Promise.allSettled(
          ids.map(id => axios.post(BASE_URL, { site_id: id, is_active: true })),
        )
        const failed = ids.filter((id, i) => results[i].status === 'rejected')
        const okCount = ids.length - failed.length

        if (failed.length) {
          const names = failed
            .map(id => siteList.value.find(s => s.id === id)?.name ?? `#${id}`)
            .join(', ')
          store.dispatch('app/setSnackBar', {
            show: true,
            text: `${okCount}/${ids.length} site(s) branché(s). Échec : ${names}`,
            color: 'error',
          })

          // On ne laisse que les échecs sélectionnés : les sites branchés sortent de
          // `availableSites`, leurs chips s'afficheraient sinon en id brut.
          selectedSiteIds.value = failed
        } else {
          store.dispatch('app/setSnackBar', {
            show: true,
            text: `${okCount} site(s) branché(s) au maillage interne`,
            color: 'success',
          })
          dialog.value = false
        }
        fetchData()
      } finally {
        saving.value = false
      }
    }

    const toggleActive = async (item, value) => {
      const isActive = !!value
      if (isActive === item.is_active) return

      togglingId.value = item.id
      try {
        await axios.put(`${BASE_URL}${item.id}`, { is_active: isActive })
        item.is_active = isActive
        store.dispatch('app/setSnackBar', {
          show: true,
          text: isActive ? 'Site réactivé' : 'Site débranché (ligne conservée)',
          color: 'success',
        })
      } catch (e) {
        notifyError(e, 'Erreur lors de la mise à jour')
        fetchData()
      } finally {
        togglingId.value = null
      }
    }

    const confirmDelete = async item => {
      const label = item.site_name || `#${item.site_id}`
      if (!confirm(`Retirer le site "${label}" du maillage interne ?`)) return

      try {
        await axios.delete(`${BASE_URL}${item.id}`)
        store.dispatch('app/setSnackBar', { show: true, text: 'Site retiré du maillage interne', color: 'success' })
        fetchData()
      } catch (e) {
        notifyError(e, 'Erreur lors de la suppression')
      }
    }

    onMounted(async () => {
      fetchData()
      if (!siteList.value.length) {
        try {
          await store.dispatch('common/getSiteList')
        } catch (e) {
          notifyError(e, 'Erreur lors du chargement de la liste des sites')
        }
      }
    })

    return {
      loading,
      saving,
      togglingId,
      entries,
      headers,
      dialog,
      selectedSiteIds,
      availableSites,
      openDialog,
      save,
      toggleActive,
      confirmDelete,
      icons: { mdiPlus, mdiTrashCan },
    }
  },
}
</script>
